import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { clinicInfo } from '@/mocks/homeData';

/* ============================================================
   ⚠️ ÁREA EDITÁVEL — Dados da Dra. Mayna Moura
   ============================================================ */

const doctorData = {
  name: 'Dra. Mayna Moura',
  role: 'Ginecologista & Obstetra',
  crm: 'CRM-CE 14202',
  image: 'https://storage.readdy-site.link/project_files/ed85e06b-2b23-49e7-a477-ed0f88fc71a8/ca03a2b0-c413-4162-9464-1f5840146eb4_compressed_Captura-de-tela-2026-07-22-152840.webp',
  bio: [
    'Especialista em Ginecologia Regenerativa e Funcional, com foco em estética íntima, menopausa e longevidade feminina. Apaixonada por devolver autoestima e qualidade de vida às mulheres através de abordagens inovadoras e baseadas em ciência.',
    'Integrou a equipe da Femme Vie em 2023, trazendo sua expertise em ginecologia regenerativa e procedimentos estéticos íntimos. Sua visão é transformar a saúde feminina através de estratégias hormonais personalizadas e tratamentos de última geração.',
    'A Dra. Mayna participa ativamente de congressos e eventos científicos — incluindo o Congresso Brasileiro de Ginecologia Regenerativa — mantendo-se sempre na vanguarda das tecnologias e técnicas mais modernas para o cuidado da mulher 40+.',
  ],
  specialties: [
    'Ginecologia Regenerativa e Funcional',
    'Estética Íntima a Laser',
    'Menopausa & Longevidade Feminina',
    'Reposição Hormonal Personalizada',
    'Histeroscopia Cirúrgica',
    'Radiofrequência Vaginal',
    'Rejuvenescimento Íntimo',
    'Saúde da Mulher 40+',
  ],
  instagramPosts: [
    {
      image: 'https://static.readdy.ai/image/5ae0a0be7c3b05867c1dbd14bfaff312/a453b92bfb1a56b3a9d4f59c11563197.png',
      caption: 'Sua saúde merece mais do que consultas comuns. Minha abordagem vai além de sintomas isolados. Ginecologia Integrativa na Femme Vie.',
      link: 'https://www.instagram.com/p/DX6-uHbDnfp/',
    },
    {
      image: 'https://static.readdy.ai/image/5ae0a0be7c3b05867c1dbd14bfaff312/f8bf5e2accd1c0852d8123a5f8b35494.png',
      caption: 'ATENÇÃO, MULHERES 40+: As mudanças na composição corporal podem começar anos antes da menopausa. Com acompanhamento adequado, é possível viver com saúde, energia e autoestima.',
      link: 'https://www.instagram.com/p/DYNpuvExha7/',
    },
    {
      image: 'https://static.readdy.ai/image/5ae0a0be7c3b05867c1dbd14bfaff312/0964a9a1c002519408a62d11024518e0.png',
      caption: '3 erros que te impedem de emagrecer na menopausa. Se você está na menopausa e não consegue perder peso, pode estar cometendo um desses erros. Saiba como emagrecer de forma saudável nessa fase.',
      link: 'https://www.instagram.com/p/DXo-UuhkdLz/',
    },
    {
      image: 'https://static.readdy.ai/image/5ae0a0be7c3b05867c1dbd14bfaff312/3b580e202929129ae260d32c26495a21.png',
      caption: 'A importância da prevenção. Como exames simples podem salvar vidas! Cuidar da saúde feminina começa com o diagnóstico precoce. Agende sua consulta de prevenção.',
      link: 'https://www.instagram.com/p/DL0goiZNqSB/',
    },
  ],
  instagram: 'https://www.instagram.com/dramaynamoura/',
  whatsapp: clinicInfo.whatsapp,
};

const testimonials = [
  {
    name: 'Patrícia L.',
    city: 'Fortaleza, CE',
    rating: 5,
    text: 'A Dra. Mayna mudou minha qualidade de vida na menopausa. O tratamento regenerativo fez uma diferença enorme nos sintomas. Me sinto renovada, com energia e autoestima de volta!',
    procedure: 'Ginecologia Regenerativa',
  },
  {
    name: 'Fernanda B.',
    city: 'Fortaleza, CE',
    rating: 5,
    text: 'Fiz o laser de rejuvenescimento vaginal e os resultados superaram todas as minhas expectativas. Profissional extremamente qualificada, ambiente impecável e um cuidado que você sente em cada detalhe.',
    procedure: 'Laser Rejuvenescimento',
  },
  {
    name: 'Mariana T.',
    city: 'Aquiraz, CE',
    rating: 5,
    text: 'Cheguei com insegurança sobre a estética íntima e saí com autoestima nas alturas. A Dra. Mayna tem uma delicadeza e um cuidado que fazem toda a diferença. O resultado é natural e lindo.',
    procedure: 'Estética Íntima',
  },
  {
    name: 'Roberta C.',
    city: 'Fortaleza, CE',
    rating: 5,
    text: 'A reposição hormonal personalizada que a Dra. Mayna prescreveu resolveu meus problemas de sono, humor e cansaço. Finalmente me sinto eu mesma de novo. Gratidão imensa!',
    procedure: 'Reposição Hormonal',
  },
];

const faqItems = [
  {
    id: 1,
    question: 'O que é Ginecologia Regenerativa?',
    answer: 'A Ginecologia Regenerativa utiliza tecnologias como laser, radiofrequência e bioestimuladores para tratar condições ginecológicas de forma minimamente invasiva, estimulando a regeneração natural dos tecidos. É indicada para flacidez vaginal, ressecamento, incontinência urinária e rejuvenescimento íntimo.',
  },
  {
    id: 2,
    question: 'Quais tratamentos de estética íntima a Dra. Mayna realiza?',
    answer: 'A Dra. Mayna realiza procedimentos com laser de CO₂, radiofrequência, bioestimuladores de colágeno e preenchedores para rejuvenescimento íntimo, tratamento de flacidez vaginal, clareamento e melhora da sensibilidade. Cada caso é avaliado individualmente em consulta.',
  },
  {
    id: 3,
    question: 'Como funciona o tratamento para menopausa com a Dra. Mayna?',
    answer: 'Após avaliação clínica e hormonal detalhada, a Dra. Mayna elabora um plano personalizado que pode incluir reposição hormonal bioidêntica, suplementação, orientação nutricional e tratamentos estéticos regenerativos, sempre visando qualidade de vida e longevidade saudável.',
  },
  {
    id: 4,
    question: 'Como agendar uma consulta com a Dra. Mayna?',
    answer: 'O agendamento pode ser feito pelo WhatsApp (85) 9 8429-3412 ou pelo telefone (85) 3045-8454. Nossa equipe está disponível de segunda a sexta, das 8h às 18h, para encontrar o melhor horário para você.',
  },
];

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          key={i}
          className={`w-3.5 h-3.5 ${i < count ? 'text-accent-400' : 'text-background-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

export default function DraMaynaMoura() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '8%']);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50 relative">
      {/* Lilás gradient background — 30% opacity */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(139,126,197,0.30) 0%, transparent 60%)',
        }}
      />

      <Navbar />
      <main className="relative z-10">
        {/* Hero Section */}
        <section ref={heroRef} className="relative pt-24 md:pt-28 pb-16 md:pb-24 bg-background-100/80 overflow-hidden">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Image with parallax */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
                className="relative"
              >
                <div className="relative rounded-lg overflow-hidden aspect-[4/5] max-w-[480px] mx-auto lg:mx-0">
                  <motion.img
                    style={{ y: imageY }}
                    src={doctorData.image}
                    alt={doctorData.name}
                    className="w-full h-[115%] object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
                </div>
                <div className="absolute -bottom-6 -right-6 w-40 h-40 rounded-full bg-primary-50/60 hidden lg:block" />
              </motion.div>

              {/* Info */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <span className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label">
                  Nossa Equipe
                </span>
                <h1 className="font-heading text-4xl md:text-6xl text-foreground-950 mt-3 leading-[1.05]">
                  {doctorData.name.split(' ').slice(0, 2).join(' ')}<br />
                  <span className="italic font-light">{doctorData.name.split(' ').slice(2).join(' ')}</span>
                </h1>
                <p className="text-xs tracking-[0.08em] uppercase text-primary-500 mt-3 font-label">
                  {doctorData.role}
                </p>
                <p className="text-xs text-foreground-400 mt-1 font-label">
                  {doctorData.crm}
                </p>

                {/* Bio */}
                <div className="mt-6 space-y-4">
                  {doctorData.bio.map((paragraph, i) => (
                    <motion.p
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + i * 0.12, duration: 0.5 }}
                      className="text-sm text-foreground-600 leading-relaxed"
                    >
                      {paragraph}
                    </motion.p>
                  ))}
                </div>

                {/* CTA Buttons */}
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <motion.a
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    href={`https://wa.me/${doctorData.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm tracking-[0.06em] uppercase bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-300 whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line text-lg"></i>
                    Agendar Consulta
                  </motion.a>
                  <motion.a
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    href={doctorData.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm tracking-[0.06em] uppercase border border-background-300/60 text-foreground-700 hover:border-primary-300 hover:text-primary-500 transition-colors duration-300 whitespace-nowrap"
                  >
                    <i className="ri-instagram-line text-lg"></i>
                    Instagram
                  </motion.a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Specialties Section */}
        <section className="relative py-20 md:py-28">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="max-w-[900px] mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12"
              >
                <span className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label">Áreas de Atuação</span>
                <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-2">
                  Especialidades &amp; <span className="italic font-light">Procedimentos</span>
                </h2>
              </motion.div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {doctorData.specialties.map((spec, index) => (
                  <motion.div
                    key={spec}
                    initial={{ opacity: 0, x: -15 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.4, delay: 0.05 * index }}
                    whileHover={{ x: 4 }}
                    className="flex items-center gap-3 px-5 py-4 rounded-lg bg-background-100/80 border border-background-200/70 hover:border-primary-200/50 hover:shadow-[0_6px_20px_-10px_rgba(0,0,0,0.04)] transition-all duration-300"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-check-line text-sm"></i>
                    </div>
                    <span className="text-sm text-foreground-700 font-label">{spec}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section — REAL PATIENTS */}
        <section className="relative py-20 md:py-28 bg-background-100/60">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <span className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label">Depoimentos</span>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-2">
                O que dizem as <span className="italic font-light">pacientes</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-[1000px] mx-auto">
              {testimonials.map((t, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: 0.08 * index }}
                  whileHover={{ y: -4 }}
                  className="group bg-background-50/90 rounded-xl p-6 border border-background-200/60 hover:border-primary-200/40 hover:shadow-[0_12px_40px_-15px_rgba(139,126,197,0.12)] transition-all duration-500"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-label">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-label text-foreground-800">{t.name}</p>
                      <p className="text-[11px] text-foreground-400">{t.city}</p>
                    </div>
                  </div>
                  <StarRating count={t.rating} />
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed italic">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-50 text-[11px] text-primary-600 font-label">
                    <i className="ri-heart-pulse-line text-[10px]"></i>
                    {t.procedure}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Instagram Posts Grid */}
        <section className="relative py-20 md:py-28">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <span className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label">Publicações Recentes</span>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-2">
                Acompanhe no <span className="italic font-light">Instagram</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {doctorData.instagramPosts.map((post, index) => (
                <motion.a
                  key={index}
                  href={post.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: 0.08 * index }}
                  whileHover={{ y: -6 }}
                  className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:shadow-[0_20px_50px_-20px_rgba(0,0,0,0.07)] transition-all duration-500"
                >
                  <div className="aspect-square overflow-hidden relative">
                    <img
                      src={post.image}
                      alt={post.caption.slice(0, 80)}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-all duration-500">
                      <span className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 text-foreground-950 opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all duration-500">
                        <i className="ri-instagram-line text-xl"></i>
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-foreground-600 leading-relaxed line-clamp-3">
                      {post.caption}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-2 text-[11px] text-primary-500 font-label whitespace-nowrap group-hover:gap-1.5 transition-all duration-300">
                      <i className="ri-instagram-line"></i>
                      Ver no Instagram
                      <i className="ri-arrow-right-line text-[10px] opacity-0 group-hover:opacity-100 transition-all duration-300"></i>
                    </span>
                  </div>
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative py-20 md:py-28 bg-background-100/60">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="max-w-[800px] mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12"
              >
                <span className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label">Perguntas Frequentes</span>
                <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-2">
                  Tire suas <span className="italic font-light">dúvidas</span>
                </h2>
              </motion.div>

              <div className="space-y-3">
                {faqItems.map((item, index) => (
                  <motion.details
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.4, delay: 0.06 * index }}
                    className="group bg-background-50/90 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200/50 transition-all duration-300 open:border-primary-200/60 open:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.04)]"
                  >
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                      <span className="text-sm font-label text-foreground-800 pr-4">{item.question}</span>
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 group-open:bg-primary-50 group-open:text-primary-500 transition-all duration-300">
                        <i className="ri-add-line text-sm group-open:hidden"></i>
                        <i className="ri-subtract-line text-sm hidden group-open:block"></i>
                      </span>
                    </summary>
                    <div className="px-6 pb-4">
                      <div className="h-[1px] bg-background-200/60 mb-4" />
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                    </div>
                  </motion.details>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(139,126,197,0.15), transparent 60%)',
            }}
          />
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 text-center relative z-10">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6 }}
              className="font-heading text-3xl md:text-4xl text-foreground-950"
            >
              Agende sua consulta com a<br />
              <span className="italic font-light">Dra. Mayna</span>
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3"
            >
              <motion.a
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                href={`https://wa.me/${doctorData.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-sm tracking-[0.06em] uppercase bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-300 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                Agendar pelo WhatsApp
              </motion.a>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
                <Link
                  to="/contato"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-sm tracking-[0.06em] uppercase border border-background-300/60 text-foreground-700 hover:border-primary-300 hover:text-primary-500 transition-colors duration-300 whitespace-nowrap"
                >
                  Enviar Mensagem
                  <i className="ri-mail-line"></i>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}